import { useAppLanguage } from '@altify/utils';
import SumsubWebSdk from '@sumsub/websdk-react';
import {
  createFileRoute,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { CustomLoader } from 'components/CustomLoader/CustomLoader';
import useCountry from 'hooks/useCountry';
import usePageConfig from 'hooks/usePageConfig';
import { useGetProfileQuery } from 'store/slices/profile-api';
import {
  useGenerateSumsubTokenQuery,
  useMarkActionAsCompleteByUserMutation,
} from 'store/slices/verification-api';
import messages from '../messages';
import BackButton from 'components/BackButton/BackButton';
import Bugsnag from '@bugsnag/js';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { rootApi } from 'store/root-api';

export const Route = createFileRoute('/_authenticated/verification/kyc')({
  component: KycVerification,
});

function KycVerification() {
  const navigate = useNavigate();
  const pageConfig = usePageConfig();
  const language = useAppLanguage();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { data: profile } = useGetProfileQuery();
  const countries = useCountry();

  const viewMessages = messages[language].profileVerification;

  const alpha3Country = countries?.find(
    (country: any) => country.code === profile?.countryCode,
  );

  const [markActionAsCompleteByUser] = useMarkActionAsCompleteByUserMutation();

  const {
    isLoading,
    data: sumsubToken,
    refetch,
  } = useGenerateSumsubTokenQuery();

  const handleMessage = async (event: any, payload: any) => {
    if (event === 'idCheck.onError') {
      navigate({ to: '/verification/error' });
    }
    if (event === 'idCheck.onApplicantStatusChanged') {
      if (payload.reviewStatus?.toUpperCase() === 'COMPLETED') {
        dispatch(rootApi.util.invalidateTags([{ type: 'UserProfile' }]));
        navigate({ to: '/verification/submitted' });
      }
    }
    if (event === 'idCheck.onStepCompleted') {
      await markActionAsCompleteByUser(payload.idDocSetType ?? '');
    }
  };

  return !isLoading ? (
    <div className="static lg:relative w-full px-4 pt-4">
      <BackButton
        className={
          'w-full text-left lg:bg-body-background mt-1 ml-0 pl-6 lg:-mt-4 pt-2 lg:ml-0 lg:pl-0 lg:pt-2'
        }
        onClick={() => router.history.back()}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-col justify-center lg:px-8 sm:px-6 py-6 lg:py-12">
            <SumsubWebSdk
              accessToken={sumsubToken!}
              expirationHandler={async () => {
                const res = await refetch();
                return res.data!;
              }}
              config={{
                lang: 'en',
                email: profile?.email,
                phone: profile?.msisdn ?? undefined,
                country: alpha3Country?.alpha3Code ?? undefined,
              }}
              options={{ addViewportTag: false, adaptIframeHeight: true }}
              onMessage={handleMessage}
              style={{
                backgroundColor: '#0C1C2C !important',
              }}
              className="bg-body-background"
              onError={e => {
                navigate({ to: '/verification/error' });
                Bugsnag.notify(e.error, (event: any) => {
                  event.addMetadata('Sumsub WebSDK', {
                    message: e.reason,
                    stack: e,
                    code: e.code,
                  });
                });
              }}
            />
            <div className="text-center mt-4 flex flex-col gap-2">
              <div className="text-center">
                <p className="text-white">
                  {viewMessages.battlingWithVerification}
                </p>
                <p className="font-extralight">
                  {viewMessages.contactUs}

                  <a
                    href={`mailto:${pageConfig?.app?.supportEmail}`}
                    className="text-secondary underline">
                    {pageConfig?.app?.supportEmail}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <CustomLoader page={true} />
  );
}

export default KycVerification;
